// Signed requests between the super console and a site's control API (issue #76).
//
// The console signs every request with the site's secret; the site checks the signature, the clock and
// that the nonce was never used before. Web Crypto only, so the same code runs in Workers and Node.
//
//   canonical string = timestamp \n nonce \n METHOD \n path+query \n sha256(body) hex
//   signature        = HMAC-SHA256(secret, canonical), hex

export const CONTROL_HEADERS = { timestamp: 'x-control-timestamp', nonce: 'x-control-nonce', signature: 'x-control-signature' } as const

/** How far a request's timestamp may be from the receiver's clock. */
export const CONTROL_MAX_SKEW_MS = 5 * 60_000

const enc = new TextEncoder()
const toHex = (buf: ArrayBuffer) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('')
const fromHex = (hex: string): Uint8Array<ArrayBuffer> | null => {
  if (!/^[0-9a-f]+$/i.test(hex) || hex.length % 2 !== 0) return null
  const out = new Uint8Array(new ArrayBuffer(hex.length / 2))
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return out
}

const sha256Hex = async (text: string) => toHex(await crypto.subtle.digest('SHA-256', enc.encode(text)))
const hmacKey = (secret: string, usage: 'sign' | 'verify') => crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [usage])

export interface ControlRequestParts {
  secret: string
  method: string
  /** Pathname plus query string, as the receiver sees it (e.g. `/api/control/usage`). */
  path: string
  body: string
  timestamp: number
  nonce: string
}

const canonical = async (p: Omit<ControlRequestParts, 'secret'>) => [p.timestamp, p.nonce, p.method.toUpperCase(), p.path, await sha256Hex(p.body)].join('\n')

export async function signControlRequest(p: ControlRequestParts): Promise<string> {
  return toHex(await crypto.subtle.sign('HMAC', await hmacKey(p.secret, 'sign'), enc.encode(await canonical(p))))
}

export type ControlVerdict = 'ok' | 'bad_signature' | 'expired'

/** Constant-time signature check plus the clock window. The caller still has to reject a reused nonce. */
export async function verifyControlRequest(p: ControlRequestParts & { signature: string; now?: number }): Promise<ControlVerdict> {
  if (!Number.isFinite(p.timestamp) || Math.abs((p.now ?? Date.now()) - p.timestamp) > CONTROL_MAX_SKEW_MS) return 'expired'
  const signature = fromHex(p.signature)
  if (!signature || p.nonce.length < 8 || p.nonce.length > 100) return 'bad_signature'
  const ok = await crypto.subtle.verify('HMAC', await hmacKey(p.secret, 'verify'), signature, enc.encode(await canonical(p)))
  return ok ? 'ok' : 'bad_signature'
}

/** Headers to send with a signed request. */
export async function controlHeaders(p: Omit<ControlRequestParts, 'timestamp' | 'nonce'> & { timestamp?: number; nonce?: string }) {
  const timestamp = p.timestamp ?? Date.now()
  const nonce = p.nonce ?? crypto.randomUUID()
  const signature = await signControlRequest({ ...p, timestamp, nonce })
  return { [CONTROL_HEADERS.timestamp]: String(timestamp), [CONTROL_HEADERS.nonce]: nonce, [CONTROL_HEADERS.signature]: signature }
}
